/**
 * @file AppContent.tsx
 * @purpose Main routing component that defines all application routes with lazy loading
 * @created 2025-01-15
 * @modified 2025-10-17
 */
import React, { Suspense } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import Hero from './layout/Hero';
import PublicLayout from './layout/PublicLayout';
import HomePage from '../pages/HomePage';
import DirektvermarkterPage from '../pages/DirektvermarkterPage';
import StandortPage from '../pages/StandortPage';
import MietenPage from '../pages/MietenPage';
import KontaktPage from '../pages/KontaktPage';
import PricingPage from '../pages/PricingPage';
import NewsletterConfirmPage from '../pages/NewsletterConfirmPage';
import VendorConfirmPage from '../pages/VendorConfirmPage';
import VendorLoginPage from '../pages/VendorLoginPage';
import DirektvermarkterUebersichtPage from '../pages/DirektvermarkterUebersichtPage';
import DirektvermarkterDetailPage from '../pages/DirektvermarkterDetailPage';
import DirektvermarkterMapPage from '../pages/DirektvermarkterMapPage';
import ProduktePage from '../pages/ProduktePage';
import WettbewerbPage from '../pages/WettbewerbPage';
import ImpressumPage from '../pages/ImpressumPage';
import DatenschutzPage from '../pages/DatenschutzPage';
import AGBPage from '../pages/AGBPage';
import FAQPage from '../pages/FAQPage';

// Admin-Komponenten - Core imports
import ProtectedRoute from './admin/ProtectedRoute';
import VendorProtectedRoute from './vendor/VendorProtectedRoute';

// Lazy load heavy admin and vendor components for better performance
const VendorDashboardPage = React.lazy(() => import('../pages/VendorDashboardPage'));
const VendorProfilePage = React.lazy(() => import('../pages/vendor/VendorProfilePage'));
const MeineBuchungenPage = React.lazy(() => import('../pages/vendor/MeineBuchungenPage'));
const VendorProductsPage = React.lazy(() => import('../pages/vendor/VendorProductsPage'));
const VendorReportsPage = React.lazy(() => import('../pages/vendor/VendorReportsPage'));
const VendorCustomerInvoicesPage = React.lazy(() => import('../pages/vendor/VendorCustomerInvoicesPage'));
const VendorInvoiceDetailPage = React.lazy(() => import('../pages/vendor/VendorInvoiceDetailPage'));
const VendorHousnkuhInvoicesPage = React.lazy(() => import('../pages/vendor/VendorHousnkuhInvoicesPage'));
const VendorFlourioDocumentsPage = React.lazy(() => import('../pages/vendor/VendorFlourioDocumentsPage'));
const VendorSettingsPage = React.lazy(() => import('../pages/vendor/VendorSettingsPage'));
const VendorForgotPasswordPage = React.lazy(() => import('../pages/vendor/VendorForgotPasswordPage'));
const VendorResetPasswordPage = React.lazy(() => import('../pages/vendor/VendorResetPasswordPage'));

// Lazy load admin components for code splitting
const LoginPage = React.lazy(() => import('../pages/admin/LoginPage'));
const SetupPage = React.lazy(() => import('../pages/admin/SetupPage'));
const DashboardPage = React.lazy(() => import('../pages/admin/DashboardPage'));
const NewsletterPage = React.lazy(() => import('../pages/admin/NewsletterPage'));
const UsersPage = React.lazy(() => import('../pages/admin/UsersPage'));
const MietfaecherPage = React.lazy(() => import('../pages/admin/MietfaecherPage'));
const VertraegeePage = React.lazy(() => import('../pages/admin/VertraegeePage'));
const ContactsPage = React.lazy(() => import('../pages/admin/ContactsPage'));
const PendingBookingsPage = React.lazy(() => import('../pages/admin/PendingBookingsPage'));
const VendorContestPage = React.lazy(() => import('../pages/admin/VendorContestPage'));
const UnauthorizedPage = React.lazy(() => import('../pages/admin/UnauthorizedPage'));
const SettingsPage = React.lazy(() => import('../pages/admin/SettingsPage'));
const TagsPage = React.lazy(() => import('../pages/admin/TagsPage'));
const FAQManagementPage = React.lazy(() => import('../pages/admin/FAQManagementPage'));
const ZusatzleistungenPage = React.lazy(() => import('../pages/admin/ZusatzleistungenPage'));
const ArticleManagementPage = React.lazy(() => import('../pages/admin/ArticleManagementPage'));
const FlourioDocumentsPage = React.lazy(() => import('../pages/admin/FlourioDocumentsPage'));
const EmailTemplatesPage = React.lazy(() => import('../pages/admin/EmailTemplatesPage'));
const InvoiceDashboard = React.lazy(() => import('../pages/admin/InvoiceDashboard'));
const StoreMapEditorPage = React.lazy(() => import('../pages/admin/StoreMapEditorPage'));

// Lazy load public store map (pulls in the three.js chunk)
const LadenkartePage = React.lazy(() => import('../pages/LadenkartePage'));

/**
 * Loading spinner component displayed while lazy-loaded components are being loaded
 * @returns {JSX.Element} Animated spinning loader with orange accent
 */
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
  </div>
);

/**
 * Layout wrapper component for public pages that includes the Hero section
 * Used specifically for the homepage to display hero banner above content
 * @returns {JSX.Element} PublicLayout with Hero section and outlet for child routes
 */
const PublicLayoutWithHero: React.FC = () => {
  return (
    <PublicLayout>
      <Hero />
      <Outlet />
    </PublicLayout>
  );
};

/**
 * Main application routing component that defines all routes with proper authentication
 * and lazy loading. Authentication providers are now globally available via SharedProviderWrapper.
 * 
 * Features:
 * - Lazy loading for performance optimization
 * - Global authentication contexts available on all routes
 * - Protected routes with role-based access control
 * - Fallback loading states for all lazy-loaded components
 * - Organized route structure: admin, vendor, and public routes
 * 
 * @returns {JSX.Element} Complete application routing structure
 */
const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Admin-Routen */}
      <Route path="/admin/login" element={
        <Suspense fallback={<LoadingSpinner />}>
          <LoginPage />
        </Suspense>
      } />
      <Route path="/admin/setup" element={
        <Suspense fallback={<LoadingSpinner />}>
          <SetupPage />
        </Suspense>
      } />
      <Route path="/admin/unauthorized" element={
        <Suspense fallback={<LoadingSpinner />}>
          <UnauthorizedPage />
        </Suspense>
      } />
      
      {/* Geschützte Admin-Routen */}
      <Route path="/admin/*" element={
        <ProtectedRoute />
      }>
        <Route index element={
          <Suspense fallback={<LoadingSpinner />}>
            <DashboardPage />
          </Suspense>
        } />
        <Route path="newsletter" element={
          <Suspense fallback={<LoadingSpinner />}>
            <NewsletterPage />
          </Suspense>
        } />
        <Route path="email-templates" element={
          <Suspense fallback={<LoadingSpinner />}>
            <EmailTemplatesPage />
          </Suspense>
        } />
        <Route path="users" element={
          <Suspense fallback={<LoadingSpinner />}>
            <UsersPage />
          </Suspense>
        } />
        <Route path="tags" element={
          <Suspense fallback={<LoadingSpinner />}>
            <TagsPage />
          </Suspense>
        } />
        <Route path="mietfaecher" element={
          <Suspense fallback={<LoadingSpinner />}>
            <MietfaecherPage />
          </Suspense>
        } />
        <Route path="vertraege" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VertraegeePage />
          </Suspense>
        } />
        <Route path="zusatzleistungen" element={
          <Suspense fallback={<LoadingSpinner />}>
            <ZusatzleistungenPage />
          </Suspense>
        } />
        <Route path="articles" element={
          <Suspense fallback={<LoadingSpinner />}>
            <ArticleManagementPage />
          </Suspense>
        } />
        <Route path="flourio-documents" element={
          <Suspense fallback={<LoadingSpinner />}>
            <FlourioDocumentsPage />
          </Suspense>
        } />
        <Route path="contacts" element={
          <Suspense fallback={<LoadingSpinner />}>
            <ContactsPage />
          </Suspense>
        } />
        <Route path="pending-bookings" element={
          <Suspense fallback={<LoadingSpinner />}>
            <PendingBookingsPage />
          </Suspense>
        } />
        <Route path="vendor-contest" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorContestPage />
          </Suspense>
        } />
        <Route path="settings" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SettingsPage />
          </Suspense>
        } />
        <Route path="faq" element={
          <Suspense fallback={<LoadingSpinner />}>
            <FAQManagementPage />
          </Suspense>
        } />
        <Route path="invoices" element={
          <Suspense fallback={<LoadingSpinner />}>
            <InvoiceDashboard />
          </Suspense>
        } />
        <Route path="ladenkarte" element={
          <Suspense fallback={<LoadingSpinner />}>
            <StoreMapEditorPage />
          </Suspense>
        } />
      </Route>
      
      {/* Vendor-Routen */}
      <Route path="/vendor/login" element={
        <VendorLoginPage />
      } />
      <Route path="/vendor/confirm" element={<VendorConfirmPage />} />
      <Route path="/vendor/forgot-password" element={
        <Suspense fallback={<LoadingSpinner />}>
          <VendorForgotPasswordPage />
        </Suspense>
      } />
      <Route path="/vendor/reset-password" element={
        <Suspense fallback={<LoadingSpinner />}>
          <VendorResetPasswordPage />
        </Suspense>
      } />
      
      {/* Geschützte Vendor-Routen */}
      <Route path="/vendor/*" element={
        <VendorProtectedRoute />
      }>
        <Route path="dashboard" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorDashboardPage />
          </Suspense>
        } />
        <Route path="profile" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorProfilePage />
          </Suspense>
        } />
        <Route path="meine-buchungen" element={
          <Suspense fallback={<LoadingSpinner />}>
            <MeineBuchungenPage />
          </Suspense>
        } />
        <Route path="products" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorProductsPage />
          </Suspense>
        } />
        <Route path="reports" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorReportsPage />
          </Suspense>
        } />
        <Route path="customer-invoices" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorCustomerInvoicesPage />
          </Suspense>
        } />
        <Route path="customer-invoices/:id" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorInvoiceDetailPage />
          </Suspense>
        } />
        <Route path="housnkuh-invoices" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorHousnkuhInvoicesPage />
          </Suspense>
        } />
        <Route path="flourio-documents" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorFlourioDocumentsPage />
          </Suspense>
        } />
        <Route path="settings" element={
          <Suspense fallback={<LoadingSpinner />}>
            <VendorSettingsPage />
          </Suspense>
        } />
      </Route>
      
      {/* Öffentliche Routen mit Hero auf der Startseite */}
      <Route path="/" element={<PublicLayoutWithHero />}>
        <Route index element={<HomePage />} />
      </Route>
      
      {/* Öffentliche Routen mit normaler Navigation */}
      <Route element={<PublicLayout><Outlet /></PublicLayout>}>
        <Route path="/produkte" element={<ProduktePage />} />
        <Route path="/direktvermarkter" element={<DirektvermarkterPage />} />
        <Route path="/direktvermarkter/uebersicht" element={<DirektvermarkterUebersichtPage />} />
        <Route path="/direktvermarkter/karte" element={<DirektvermarkterMapPage />} />
        <Route path="/direktvermarkter/:id" element={<DirektvermarkterDetailPage />} />
        <Route path="/vendors" element={<WettbewerbPage />} />
        <Route path="/standort" element={<StandortPage />} />
        <Route path="/ladenkarte" element={
          <Suspense fallback={<LoadingSpinner />}>
            <LadenkartePage />
          </Suspense>
        } />
        <Route path="/mieten" element={<MietenPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/kontakt" element={<KontaktPage />} />
        <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/agb" element={<AGBPage />} />
        <Route path="/faq" element={<FAQPage />} />
      </Route>
    </Routes>
  );
};

export default AppContent;